import { Typography, withStyles, WithStyles, Theme, Button } from '@material-ui/core'
import { Fragment } from 'react'
import { UpdateStatus } from '../../../actions/types'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { BucketObject } from '../../../services/storage/types'
import i18n from '../../../translations/i18n'
import { Apartment } from '../../../types/apartments'
import { IndexedApplications } from '../../../types/states'
import { User } from '../../../types/users'
import { AddressAndMedia, ApartmentInfo, ApartmentAdminPanel } from './Containers'
import Suggest from '../Stepper/Steps/Suggest'

const styles = ({ breakpoints, typography }: Theme) => ({
  primaryBtn: {
    backgroundColor: '#94004A',
    padding: '8px',
    '&:hover': {
      backgroundColor: '#670033'
    }
  },
  root: {
    backgroundColor: '#ffffff',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
    [breakpoints.down('sm')]: {
      padding: '0 16px 28px'
    }
  },
  title: {
    textAlign: 'center' as any,
    margin: '0.5em 0.5em 0.1em 0.35em'
  },
  infoSectionContent: {
    width: '60%',
    maxWidth: '60%',
    [breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%'
    }
  },
  sectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.01em',
    margin: '32px 0 8px',
    cursor: 'pointer',
    userSelect: 'none' as const
  },
  sectionIcon: {
    fontSize: '1.25rem',
    color: '#6D6D6D',
    lineHeight: 1
  },
  sectionDivider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#E5E5E5',
    marginBottom: '24px'
  },
  informationContainer: {
    maxWidth: '100%'
  },
  descriptionContainer: {
    marginBottom: '32px'
  },
  adminPanelContainer: {
    textAlign: 'left' as const
  },
  adminPanelActions: {
    marginLeft: 'auto',
    marginTop: '12px'
  },
  addressAndMedia: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  mediaActionsRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  mediaSliderContainer: {
    width: '100%',
    maxWidth: '1020px',
    margin: '0 auto'
  },
  addressAndMediaTitle: {
    padding: '8px 0px 8px'
  },
  panelSection: {
    width: '100%',
    margin: '0 0 32px 0',
    backgroundColor: '#e2ebfd',
    padding: '48px 0px',
    '& > div': {
      width: '100%',
      maxWidth: '1020px',
      margin: '0 auto',
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '60px'
    },
    '@media (max-width: 1064px)': {
      '& > div': {
        flexDirection: 'column' as const
      }
    },
    [breakpoints.down('sm')]: {
      margin: '0 -16px 32px',
      padding: '32px 16px',
      '& > div': {
        gap: '20px'
      }
    }
  },
  panelSectionColumn: {
    flex: '1 1 360px',
    minWidth: '280px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    '&:last-child': {
      justifyContent: 'flex-end'
    },
    '@media (max-width: 1064px)': {
      flex: '1 1 100%',
      minWidth: '100%'
    },
    '&:first-child': {
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      '& > *': {
        width: '100%'
      }
    }
  },
  panelSectionSubtitle: {
    fontSize: '1rem',
    color: '#2f2f2f',
    marginBottom: '10px'
  },
  highlightTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    margin: 0,
    [breakpoints.down('xs')]: {
      fontSize: '20px'
    }
  },
  highlightMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
    [breakpoints.down('xs')]: {
      justifyContent: 'center',
      textAlign: 'center' as const,
      gap: '5px'
    }
  },
  highlightMetaItem: {
    minWidth: '120px'
  },
  highlightMetaLabel: {
    fontSize: '1.25rem',
    lineHeight: '1rem',
    marginBottom: '4px',
    [breakpoints.down('xs')]: {
      fontSize: '18px'
    }
  },
  highlightMetaValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    [breakpoints.down('sm')]: {
      fontSize: '20px'
    }
  },
  highlightSection: {
    width: '100%',
    maxWidth: '1020px',
    margin: '0 auto 32px',
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '32px',
    [breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center' as const
    }
  },
  heroAddressBlock: {
    flex: '1 1 auto',
    [breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  mediaSection: {
    width: '100%',
    marginBottom: '32px'
  },
  mediaSectionContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  detailsSection: {
    width: '100%',
    maxWidth: '1020px',
    margin: '0 auto'
  },
  contactSection: {
    width: '100%',
    maxWidth: '1020px',
    margin: '32px auto 72px auto',
    boxSizing: 'border-box',
    [breakpoints.down('md')]: {
      margin: '32px auto 12px auto'
    }
  },
  contactCard: {
    padding: '25px',
    boxSizing: 'border-box',
    borderRadius: '12px',
    boxShadow: '0px 1px 5px 0px rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  contactText: {
    fontSize: '1.2rem',
    fontWeight: 600
  },
  contactButton: {
    backgroundColor: '#94004A',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '999px',
    textTransform: 'uppercase' as const,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#670033'
    },
    [breakpoints.down('xs')]: {
      width: 'auto',
      alignSelf: 'flex-start',
      marginLeft: 0,
      minWidth: 'fit-content'
    }
  },
  line: {
    margin: '0',
    padding: '0',
    width: '100%',
    borderBottom: '1px solid #E5E5E5'
  },
  cancelApartment: {
    backgroundColor: '#fff',
    marginBottom: '10px',
    color: '#ff0000',
    border: '1px solid #ff0000',
    padding: '8px',
    width: '100%',
    fontSize: ' 0.8125rem',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#fff',
      color: '#c30e0d',
      border: '1px solid #c30e0d',
      boxShadow: 'none'
    }
  },
  startMarketing: {
    backgroundColor: '#94004A',
    color: '#fff',
    width: '100%',
    marginBottom: '10px',
    border: '1px solid #94004A',
    padding: '8px',
    fontSize: ' 0.8125rem',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#670033',
      //color: '#670033',
      border: '1px solid #670033',
      boxShadow: 'none'
    }
  },
  followUpButton: {
    textTransform: 'uppercase' as const,
    width: '100%'
  },
  disabled: {
    border: 'none'
  },
  mt20: {
    marginTop: '20px'
  },

  //STYLES FOR END RENT DIALOG

  acceptBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '25px 18px',
    gap: '12px',
    width: '100%',
    //maxWidth: 600,
    background: '#F7F8F9',
    borderRadius: 20,
    marginTop: 6,
    marginBottom: 8,
    [breakpoints.down('xs')]: {
      padding: '16px 16px',
      maxWidth: '100%'
    }
  },
  digivakuusFaqButton: {
    marginTop: 8,
    marginLeft: 0,
    padding: 0,
    fontSize: 17,
    textTransform: 'none' as const,
    textDecoration: 'underline',
    fontWeight: 600,
    color: '#94004A',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  digivakuusFaqInlineWrapper: {
    display: 'flex',
    width: '100%',
    marginTop: 4
  },

  // NEW — row holding the checkbox and text
  acceptRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    width: '100%'
  },

  // NEW — custom checkbox styling (30x30, brand color)
  checkboxRoot: {
    width: 30,
    height: 30,
    padding: 0,
    margin: 0,
    color: '#94004A',
    '&$checkboxChecked': {
      color: '#94004A'
    }
  },
  checkboxChecked: {},

  // NEW — label text styles from your spec, but responsive
  acceptTexts: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    color: "#000"
  },
  acceptHeader: {
    fontFamily: "Circular Std, Inter, system-ui, Arial, sans-serif",
    fontWeight: 700,
    fontSize: '18px',
    marginTop: "2px",
    //fontSize: 18,
    lineHeight: "24px",
  },
  acceptText: {
    fontFamily: "CircularRegular, Inter, system-ui, Arial, sans-serif",
    //fontSize: 16,
    lineHeight: "1.5",
    marginTop: 22,
    color: "#2F2F2F",
    fontSize: '16px',
    fontWeight: 400,
    whiteSpace: 'pre-line'
  },
  cashbackBox: {
    marginTop: 16,
    marginLeft: '-5px',
    padding: '12px 16px',
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    border: '1px solid #94004A'
  },
  cashbackTitle: {
    fontFamily: "Circular Std, Inter, system-ui, Arial, sans-serif",
    fontWeight: 700,
    fontSize: 16,
    color: '#94004A'
  },
  cashbackText: {
    marginTop: 4,
    fontFamily: "CircularRegular, Inter, system-ui, Arial, sans-serif",
    fontSize: 16,
    lineHeight: "1.5",
    color: '#2F2F2F'
  }
})

interface ApartmentInformationProps extends WithStyles {
  apartment: Apartment
  user: User | undefined
  files: BucketObject[]
  t: i18n.TFunction
  isAdmin: boolean
  updateStatus: UpdateStatus
  applications: IndexedApplications
  containerStyle?: React.CSSProperties
  hideLandlordPanel?: boolean
}

const ApartmentInformation = ({
  t,
  classes,
  apartment,
  user,
  files,
  isAdmin,
  updateStatus,
  applications,
  containerStyle,
  hideLandlordPanel
}: ApartmentInformationProps) => {
  const safeApplications = applications || ({} as IndexedApplications)
  const safeUpdateStatus: UpdateStatus =
    updateStatus || ((() => undefined) as UpdateStatus)
  const code = apartment?.code
  const district = apartment?.information?.district
  const city = apartment?.information?.postalArea
  const heroLocationLine =
    district && city ? `${district}, ${city}` : district || city || ''
  const heroStreet = [
    apartment?.information?.streetAddress,
    apartment?.information?.streetNumber
  ]
    .filter(Boolean)
    .join(' ')
  const heroAddress =
    [heroStreet, heroLocationLine]
      .filter(Boolean)
      .join(', ') || heroStreet || t('apartment.propertyInformation')
  const buildingTypeKey = apartment?.information?.buildingType
    ? `apartment.buildingType.${apartment.information.buildingType}`
    : undefined
  const resolvedBuildingTypeLabel = buildingTypeKey
    ? (() => {
        const translation = t(buildingTypeKey)
        return translation !== buildingTypeKey
          ? translation
          : apartment?.information?.buildingType || ''
      })()
    : undefined
  const buildingTypeLabel =
    resolvedBuildingTypeLabel && resolvedBuildingTypeLabel.trim().length > 0
      ? resolvedBuildingTypeLabel
      : t('apartment.propertyInformation')
  const roomsLabel =
    typeof apartment?.information?.rooms === 'string'
      ? apartment.information.rooms
      : ''
    const kitchenLabel =
    typeof apartment?.information?.kitchen === 'string'
      ? apartment.information.kitchen.toLowerCase()
      : ''
  const saunaLabel =
    apartment?.information?.sauna && apartment.information.sauna !== 'no'
      ? '+ s'
      : ''
  let balconyLabel = ''
  if (apartment?.information?.balcony && apartment.information.balcony !== 'no') {
    balconyLabel =
      apartment.information.balcony === 'glass'
        ? '+ lasit. p'
        : apartment.information.balcony === 'balconet'
        ? '+ ransk. p'
        : '+ p'
  }
  const closetLabel =
    apartment?.information?.hasBuiltInClosetCheckBoxProperty ? '+ vh' : ''
  const roomKitchenSegment = [
    roomsLabel,
    kitchenLabel && `+ ${kitchenLabel}`,
    saunaLabel,
    balconyLabel,
    closetLabel
  ]
    .filter((value) => value && value.trim().length > 0)
    .join(' ')
  const heroSubtitle = [buildingTypeLabel, roomKitchenSegment]
    .filter((value) => value && value.trim().length > 0)
    .join(' | ')

  const heroMetaItems: { label: string; value: string }[] = [
    {
      label: t('apartment.list.information.overview.rent'),
      value:
        typeof apartment?.confirmation?.price === 'number'
          ? `${apartment.confirmation.price.toLocaleString('fi-FI')} €/kk`
          : '-'
    },
    {
      label: t('apartment.list.information.overview.size'),
      value:
        typeof apartment?.information?.squareMeters === 'number'
          ? `${apartment.information.squareMeters
              .toFixed(1)
              .toString()
              .replace('.', ',')} m²`
          : '-'
    },
    {
      label: t('apartment.list.information.overview.year'),
      value: apartment?.information?.buildYear
        ? apartment.information.buildYear.toString()
        : '-'
    }
  ]

  return (
    <div
      className={classes.root}
      style={{ overflow: 'hidden', ...containerStyle }}
    >
      {apartment?.information?.address && (
        <Fragment>
          {!hideLandlordPanel && (
            <section className={classes.panelSection}>
              <div>
                <div className={classes.panelSectionColumn}>
                  <Typography variant='h5' className={classes.panelSectionTitle}>
                    {t('apartment.propertyInformation')}
                  </Typography>
                  <Typography className={classes.panelSectionSubtitle}>
                    {t('apartment.propertyInformationSubtitle')}
                  </Typography>
                  <Suggest
                    content={t('apartmentOverview.suggestBoxContent')}
                    containerMaxWidth='100%'
                    contentMaxWidth='100%'
                    desktopMaxWidth='100%' 
                    alignLeftOnDesktop
                    padding='0'
                    disableAutoMargin
                  />
                </div>
                <div className={classes.panelSectionColumn}>
                  <ApartmentAdminPanel
                    t={t}
                    classes={classes}
                    apartment={apartment}
                    user={user}
                    isAdmin={isAdmin}
                    updateStatus={safeUpdateStatus}
                    applications={safeApplications}
                    files={files}
                  />
                </div>
              </div>
            </section>
          )}
          <section className={classes.mediaSection}>
            <div className={classes.mediaSectionContent}>
              <AddressAndMedia
                t={t}
                files={files}
                classes={classes}
                apartment={apartment}
              />
            </div>
          </section>
          <section className={classes.highlightSection}>
            <div className={classes.heroAddressBlock}>
              <Typography className={classes.highlightMetaLabel}>
                {heroSubtitle}
              </Typography>
              <Typography className={classes.highlightTitle}>
                {heroAddress}
              </Typography>
            </div>
            <div className={classes.highlightMeta}>
              {heroMetaItems.map(({ label, value }) => (
                <div
                  key={`${label}-${value}`}
                  className={classes.highlightMetaItem}
                >
                  <Typography className={classes.highlightMetaLabel}>
                    {label}
                  </Typography>
                  <Typography className={classes.highlightMetaValue}>
                    {value}
                  </Typography>
                </div>
              ))}
            </div>
          </section>
          <section className={classes.detailsSection}>
            <ApartmentInfo
              t={t}
              classes={classes}
              apartment={apartment}
              user={user}
              updateStatus={safeUpdateStatus}
              isAdmin={isAdmin}
              applications={safeApplications}
              files={files}
            />
          </section>
          {code && (
            <section className={classes.contactSection}>
              <div className={classes.contactCard}>
                <Typography className={classes.contactText}>
                  {t('apartment.contactSection.text')}
                </Typography>
                <Button
                  className={classes.contactButton}
                  component='a'
                  href={`/new-application/${code}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  endIcon={<OpenInNewIcon fontSize='small' />}
                >
                  {t('apartment.contactSection.cta')}
                </Button>
              </div>
            </section>
          )}
        </Fragment>
      )}
    </div>
  )
}

export default withStyles(styles)(ApartmentInformation)
